<template>
  <div
    class="so-data-table"
    :class="{
      'so-data-table--card': card,
      'so-data-table--loading': isLoading,
      'so-data-table--row-clickable': rowClickable,
    }"
    data-testid="so-data-table"
    :aria-busy="isLoading"
    v-bind="$attrs"
  >
    <v-data-table-server
      ref="table"
      v-model="selectedItems"
      :headers="dataTableHeaders"
      :items="items"
      :items-per-page="innerOptions.pageSize"
      :items-length="totalItems"
      :items-per-page-options="itemsPerPageOptions"
      :item-value="selectKey"
      :item-selectable="isItemSelectable"
      :page="innerOptions.page"
      :sort-by="sortByMapped"
      :show-select="selectable"
      :select-strategy="selectStrategy"
      :hover="rowClickable"
      :return-object="!selectKey"
      :loading="isLoading"
      :density="compact ? 'compact' : 'default'"
      class="rounded-lg"
      data-testid="so-data-table-server-table"
      fixed-header
      :show-expand="expandable"
      @update:options="onUpdateOptions"
      @click:row="onRowClick"
    >
      <template v-if="$slots.prependBody" #body.prepend>
        <tr>
          <td colspan="100%">
            <slot name="prependBody" v-bind="data" />
          </td>
        </tr>
      </template>
      <template #top>
        <slot name="top">
          <div
            v-if="hasFilters || searchable || $slots.tableActions"
            class="d-flex align-start gap-3 pb-5"
          >
            <SoDataTableFiltersDropdown
              v-if="hasFilters"
              v-model:filters="innerOptions.filters"
              :filter-width="filterWidth"
              :loading="areFiltersLoading"
            />

            <div
              class="d-flex align-start gap-1"
              :class="searchAlignEnd ? 'ml-auto pr-1' : 'pl-1'"
            >
              <slot name="beforeSearch"></slot>
              <form v-if="searchable" class="d-flex gap-1" @submit.prevent>
                <SoTextField
                  v-model="innerSearchQuery"
                  :placeholder="searchPlaceholder"
                  :style="{ minWidth: autoSearch ? '240px' : '220px' }"
                  :append-icon="autoSearch ? 'search' : undefined"
                  data-testid="so-data-table-search"
                  type="text"
                  clearable
                  hide-details
                  autofocus
                />
                <SoButton
                  v-if="!autoSearch"
                  :disabled="!isSearchQueryDirty || isLoading"
                  type="primary"
                  data-testid="so-data-table-search-button"
                  icon="search"
                  large
                  submit
                  @click="innerOptions.search = innerSearchQuery?.trim() ?? ''"
                />
              </form>
            </div>

            <slot name="tableActions"></slot>
          </div>
          <SoDataTableFiltersChips v-model:filters="innerOptions.filters" />
          <slot name="doughnutChart"></slot>
        </slot>
      </template>

      <template
        #header.data-table-select="{ selectAll, allSelected, someSelected }"
      >
        <v-checkbox-btn
          v-if="selectStrategy !== 'single'"
          class="so-data-table__header"
          :value-comparator="
            () => allSelected && canSelectAll(allSelected, someSelected)
          "
          :indeterminate="someSelected && !allSelected"
          color="success"
          tabindex="0"
          hide-details="auto"
          :disabled="
            !canSelectAll(allSelected, someSelected) || selectAllDisabled
          "
          @click="(selectAll(!allSelected), $emit('allSelected', !allSelected))"
          @update:indeterminate="null"
        />
      </template>

      <template
        #item.data-table-select="{ internalItem, isSelected, toggleSelect }"
      >
        <v-radio
          v-if="selectStrategy === 'single'"
          :value-comparator="() => isSelected(internalItem)"
          :disabled="internalItem.selectable === false"
          color="success"
          tabindex="0"
          :readonly="isSelected(internalItem)"
          @click="toggleSelect(internalItem)"
        />
        <v-checkbox-btn
          v-else
          :value-comparator="() => isSelected(internalItem)"
          :disabled="internalItem.selectable === false"
          color="success"
          tabindex="0"
          hide-details="auto"
          @click="(toggleSelect(internalItem), $emit('allSelected', false))"
        />
      </template>

      <template
        v-for="header in headers"
        #[`header.${header.key}`]="{ column, isSorted, getSortIcon }"
        :key="`${header.key}`"
      >
        <div class="so-data-table__header">
          <v-hover v-slot="{ isHovering, props }">
            <span v-bind="props" class="d-flex align-center">
              <slot :name="`header.${header.key}`" v-bind="{ column }">
                <SoDataTableCell :value="column.title" />
              </slot>
              <component
                :is="header.icon ? 'SoTooltip' : 'span'"
                :text="getIconTooltip(header.icon)"
              >
                <SoIcon
                  v-if="getIconName(header.icon)"
                  :icon="getIconName(header.icon)"
                  size="x-small"
                  class="ml-2"
                />
              </component>
              <v-icon
                v-if="column.sortable"
                class="so-data-table__icon"
                :class="{
                  'so-data-table__icon--hide': !isHovering && !isSorted(column),
                  [`so-data-table__icon--align-${column.align}`]: column.align,
                }"
                :icon="getSortIcon(column) as string"
                :color="!isSorted(column) ? 'default' : ''"
              ></v-icon>
            </span>
          </v-hover>
        </div>
      </template>

      <template
        v-for="column in headers"
        #[`item.${column.key}`]="{ internalItem }"
        :key="`${column.key}`"
      >
        <div
          data-test="so-data-table-cell"
          :class="{ color: internalItem.raw.rowColor }"
          :style="{
            backgroundColor:
              (internalItem.raw.rowColor as string) || 'transparent',
          }"
        >
          <slot :name="`item.${column.key}`" v-bind="{ internalItem }">
            <SoDataTableCell
              :value="internalItem.raw[column.key]"
              :type="column.type"
              :fallback-value="column.fallbackValue"
            />
          </slot>
        </div>
      </template>

      <template #item.actions="rowData">
        <slot name="itemActions" v-bind="rowData"></slot>
      </template>

      <template #no-data>
        <div
          v-if="!$slots.noData"
          class="text-center text-grey py-16"
          data-testid="so-data-table-no-data"
        >
          <SoIcon icon="circle-off" size="x-large" />
          <p class="text-h5 mt-3">{{ noDataText }}</p>
        </div>
        <slot name="noData"></slot>
      </template>

      <template v-if="!items.length" #loading>
        <div class="d-flex py-16 justify-center text-h3 text-grey">
          {{ loadingText }}
        </div>
      </template>

      <template #footer.prepend>
        <div
          v-if="items.length"
          data-testid="so-data-table-copy"
          class="mr-auto"
        >
          <SoCopyToClipboard
            v-if="showCopyToClipboard"
            :element-to-copy="tableContents"
          />
        </div>
      </template>

      <template v-if="expandable" #expanded-row="{ item }">
        <slot name="expandableData" v-bind="item"></slot>
      </template>
    </v-data-table-server>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import { VDataTableServer } from "vuetify/components"
  import {
    FilterType,
    SoDataTableColumnType as ColumnType,
    SoDataTableFilters,
    SoDataTableHeader,
    SoDataTableItem,
    SoDataTableOrderBy,
    SoDataTableSelectableItems,
    SoDataTableSortItem,
  } from "@/types/components/SoDataTable"
  import { SoChipType } from "@/types/components/SoChip"
  import SoCopyToClipboard, {
    CopyToClipboardElement,
  } from "../SoCopyToClipboard.vue"
  import { DEFAULT_PAGINATION_SIZE } from "@/constants/general"
  import SoIcon from "@/components/SoIcon.vue"
  import SoDataTableCell from "./SoDataTableCell.vue"
  import SoDataTableFiltersDropdown from "./SoDataTableFiltersDropdown.vue"
  import SoDataTableFiltersChips from "./SoDataTableFiltersChips.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { cloneDeep, debounce, isEqual } from "lodash-es"
  import { isEmpty } from "@/helpers/objectHelpers"
  import SoTooltip from "../SoTooltip.vue"

  const MAX_REQUESTS_PER_SECOND = 3

  export type itemsPerPageOption = {
    title: string
    value: number
  }
  const itemsPerPageOptions: itemsPerPageOption[] = [
    { title: "10", value: 10 },
    { title: "25", value: 25 },
    { title: "50", value: 50 },
    { title: "100", value: 100 },
  ]

  export type SoDataTableOptions<T = SoDataTableFilters> = {
    page: number
    pageSize: number
    search: string
    filters: T
    orderBy: SoDataTableOrderBy
    hasAnyFilters?: boolean
  }

  export type SoDataTableData = {
    isLoading: boolean
    items: SoDataTableItem[]
    totalItems: number
  }

  export type SelectStrategy = "all" | "page" | "single"

  type FetchCallback = (options: SoDataTableOptions) => void

  export default defineComponent({
    name: "SoDataTableServer",

    components: {
      SoButton,
      SoCopyToClipboard,
      SoDataTableCell,
      SoTextField,
      SoIcon,
      SoDataTableFiltersDropdown,
      SoDataTableFiltersChips,
      SoTooltip,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: Array as PropType<SoDataTableItem[] | unknown[]>,
        default: () => [],
      },
      headers: {
        type: Array as PropType<SoDataTableHeader[]>,
        default: () => [],
      },
      options: {
        type: Object as PropType<Partial<SoDataTableOptions>>,
        default: undefined,
      },
      isShowAllPaginationOption: {
        type: Boolean,
        default: false,
      },
      query: {
        type: Object,
        default: undefined,
      },
      defaultItemsPerPage: {
        type: Number,
        default: undefined,
      },
      searchable: {
        type: Boolean,
        default: false,
      },
      autoSearch: {
        type: Boolean,
        default: true,
      },
      selectable: {
        type: Boolean,
        default: false,
      },
      isItemSelectable: {
        type: [Function] as PropType<SoDataTableSelectableItems>,
        default: undefined,
      },
      selectAllDisabled: {
        type: Boolean,
        default: false,
      },
      selectKey: {
        type: String,
        default: "id",
      },
      selectStrategy: {
        type: String as PropType<SelectStrategy>,
        default: "page",
      },
      noDataText: {
        type: String,
        default: "So empty",
      },
      loadingText: {
        type: String,
        default: "Please wait while we load the data",
      },
      actionsHeaderText: {
        type: String,
        default: "Actions",
      },
      rowClickable: {
        type: Boolean,
        default: false,
      },
      compact: {
        type: Boolean,
        default: false,
      },
      showCopyToClipboard: {
        type: Boolean,
        default: false,
      },
      searchPlaceholder: {
        type: String,
        default: "Search",
      },
      /** Push the search field (and any table actions) to the right edge. */
      searchAlignEnd: {
        type: Boolean,
        default: false,
      },
      data: {
        type: Object as PropType<SoDataTableData>,
        default: () => ({
          isLoading: false,
          totalItems: 0,
          items: [],
        }),
      },
      card: {
        type: Boolean,
        default: false,
      },
      areFiltersLoading: {
        type: Boolean,
        default: false,
      },
      filterWidth: {
        type: String,
        default: undefined,
      },
    },

    emits: [
      "click:row",
      "update:modelValue",
      "update:options",
      "allSelected",
      "fetch",
    ],
    data() {
      return {
        SoChipType,
        ColumnType,
        FilterType,
        items: [] as SoDataTableItem[],
        selectedItems: [] as SoDataTableItem[] | unknown[],
        tableContents: null as CopyToClipboardElement,
        totalItems: 0,
        innerAllItemsSelected: false,
        skipNextRefetch: false,
        debouncedFetch: null as FetchCallback | null,
        innerSearchQuery: "",
        jumpToPage: null as number | null,
        innerOptions: {
          page: 1,
          pageSize: this.defaultItemsPerPage ?? DEFAULT_PAGINATION_SIZE,
          search: "",
          filters: {},
          orderBy: {},
          hasAnyFilters: false,
        } as SoDataTableOptions,
      }
    },

    computed: {
      isSearchQueryDirty() {
        const newVal = this.innerSearchQuery?.trim() ?? ""
        const oldVal = this.options?.search?.trim() ?? ""
        return newVal !== oldVal
      },
      itemsPerPageOptions() {
        return this.isShowAllPaginationOption
          ? [
              ...itemsPerPageOptions,
              { title: "Show all", value: this.totalItems },
            ]
          : itemsPerPageOptions
      },
      rawOptions() {
        return cloneDeep(this.innerOptions)
      },
      sortByMapped(): SoDataTableSortItem[] | undefined {
        return Object.entries(this.innerOptions.orderBy).map(
          ([sortKey, order]) => {
            const key = this.headers.find((h) => h.sortKey === sortKey)?.key
            return {
              key: key ?? sortKey,
              order,
            }
          },
        )
      },
      isLoading(): boolean {
        return !!this.data?.isLoading
      },
      hasFilters() {
        return Object.keys(this.innerOptions.filters).length
      },
      hasAnyFilters() {
        const hasFilters = !Object.keys(this.innerOptions.filters).reduce(
          (overall, filterKey) => {
            const filter = this.innerOptions.filters[filterKey]
            let isEmpty = true
            if (filter.type === FilterType.CHECKBOX) {
              isEmpty = filter.value.length === 0
            } else if (filter.type === FilterType.DATE_RANGE) {
              isEmpty =
                JSON.stringify(filter.value) ===
                JSON.stringify({ from: null, to: null })
            } else {
              isEmpty = filter.value === null
            }
            return overall && isEmpty
          },
          true,
        )

        return hasFilters || !!this.innerOptions.search
      },
      dataTableHeaders(): SoDataTableHeader[] {
        const headers: SoDataTableHeader[] = this.headers
          .filter((h) => h.isVisible !== false)
          .map((header) => ({
            key: header.key,
            title: header.title,
            colspan: header.colspan,
            rowspan: header.rowspan,
            align: header.align,
            sortable: header.sortable,
            icon: header.icon,
          }))

        if (this.$slots.itemActions) {
          headers.push({
            key: "actions",
            title: this.actionsHeaderText,
            align: "end",
            sortable: false,
          })
        }

        return headers
      },
      expandable() {
        return !!this.$slots.expandableData
      },
    },

    watch: {
      innerSearchQuery(newSearchQuery: string) {
        if (this.autoSearch || !newSearchQuery) {
          this.innerOptions.search = newSearchQuery?.trim() ?? ""
        }
      },
      selectedItems(newSelectedItems: SoDataTableItem[]) {
        this.$emit("update:modelValue", newSelectedItems)
      },
      modelValue(newModelValue: SoDataTableItem[]) {
        this.selectedItems = newModelValue
      },
      options: {
        deep: true,
        handler(newOptions: SoDataTableOptions) {
          this.innerOptions = newOptions
        },
      },
      innerOptions: {
        deep: true,
        handler(newOptions: SoDataTableOptions) {
          this.$emit("update:options", newOptions)
        },
      },
      isShowAllPaginationOption(isShowAll) {
        if (!isShowAll) {
          this.innerOptions.pageSize =
            this.defaultItemsPerPage ?? DEFAULT_PAGINATION_SIZE
        }
      },
      rawOptions(
        newOptions: SoDataTableOptions,
        oldOptions: SoDataTableOptions,
      ) {
        if (this.skipNextRefetch) {
          this.skipNextRefetch = false
          return
        }
        if (isEqual(newOptions, oldOptions)) return
        const filtersChanged =
          !isEqual(newOptions.filters, oldOptions.filters) &&
          !isEmpty(oldOptions.filters)
        if (filtersChanged && this.innerOptions.page !== 1) {
          this.innerOptions.page = 1
        } else {
          this.debouncedFetch?.(this.innerOptions)
        }
        // Reset selection if filters changed
        if (filtersChanged && this.selectedItems.length) {
          this.$emit("allSelected", false)
          this.selectedItems = []
        }
      },
      data: {
        deep: true,
        handler(newData: SoDataTableData) {
          if (!newData.isLoading) {
            this.items = newData.items ?? []
            this.totalItems = newData.totalItems ?? 0
            if (this.jumpToPage) {
              this.innerOptions.page = this.jumpToPage
              this.jumpToPage = null
            }
          }
        },
      },
    },

    created() {
      if (this.modelValue) {
        this.selectedItems = this.modelValue
      }
      if (this.options) {
        this.innerOptions = {
          ...this.innerOptions,
          ...cloneDeep(this.options),
        }
        if (this.options.page && this.options.page !== 1) {
          this.jumpToPage = this.options.page
        }
      }
    },
    mounted() {
      this.items = this.data.items
      this.setTableContents()
      this.fetch(this.innerOptions)
      this.setupDebouncedFetch()
      this.innerSearchQuery = this.options?.search ?? ""
    },
    methods: {
      setTableContents() {
        const table = this.$refs.table as typeof VDataTableServer
        this.tableContents = table.$el.querySelector("table")
      },
      setupDebouncedFetch() {
        const debounceDelay = 1000 / MAX_REQUESTS_PER_SECOND
        this.debouncedFetch = debounce(this.fetch, debounceDelay, {
          leading: true,
        })
      },
      fetch(newOptions: SoDataTableOptions) {
        this.$emit("fetch", {
          ...newOptions,
          hasAnyFilters: this.hasAnyFilters,
        })
      },
      onUpdateOptions({ page, itemsPerPage, sortBy }) {
        if (this.isLoading) return
        this.innerOptions.page = page
        this.innerOptions.pageSize = itemsPerPage
        this.innerOptions.orderBy = this.mapOrderBy(sortBy)
      },
      mapOrderBy(sortBy: SoDataTableSortItem[]): SoDataTableOrderBy {
        return sortBy.reduce((acc, item) => {
          const col = this.headers.find((h) => h.key === item.key)
          const sortKey = col?.sortKey ?? col?.key

          if (sortKey && (item.order === "asc" || item.order === "desc")) {
            acc[sortKey] = item.order
          }

          return acc
        }, {} as SoDataTableOrderBy)
      },
      onRowClick(event: Event, { internalItem }: unknown) {
        // Rows only respond to clicks when explicitly opted in via rowClickable.
        if (!this.rowClickable) return
        if (this.selectable && this.selectStrategy === "single") {
          this.selectedItems = [internalItem.value]
        }
        this.$emit("click:row", internalItem.raw)
      },
      resetPagination() {
        this.innerOptions.page = 1
      },
      refetch(resetPagination = false) {
        this.$emit("allSelected", false)
        this.selectedItems = []
        if (resetPagination) {
          this.resetPagination()
        }
        this.fetch(this.innerOptions)
      },
      canSelectAll(allSelected: boolean, someSelected: boolean) {
        return (
          (!allSelected && !someSelected) ||
          (allSelected && someSelected) ||
          (!allSelected && someSelected)
        )
      },
      setOptions(newOptions: Partial<SoDataTableOptions>, refetch = true) {
        this.skipNextRefetch = !refetch
        this.innerOptions = {
          ...this.innerOptions,
          ...newOptions,
        }
      },
      getIconName(icon) {
        return typeof icon === "string" ? icon : icon?.name
      },
      getIconTooltip(icon) {
        return typeof icon === "object" ? icon?.tooltip : undefined
      },
      getIconComponent(icon) {
        return this.getIconTooltip(icon) ? "SoTooltip" : "span"
      },
    },
  })
</script>

<style scoped>
  @import url("./SoDataTable.css");
</style>
